import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Chip, Tabs, Tab, Grid,
  IconButton, Tooltip, Alert, CircularProgress, MenuItem, Divider, FormControlLabel, Checkbox,
  InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import HistoryIcon from '@mui/icons-material/History';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import axios from 'axios';
import { consultaDni, consultaRuc } from '../../utils/consultaPeru';

const TIPOS_DOC = ['DNI', 'CE', 'PASAPORTE', 'RUC'];
const REGIMENES = ['GENERAL', 'MYPE', 'AGRARIO'];
const TIPOS_CONTRATO = ['INDEFINIDO', 'PLAZO_FIJO', 'PRACTICAS', 'LOCACION'];
const ESTADOS_CIVIL = ['SOLTERO', 'CASADO', 'CONVIVIENTE', 'DIVORCIADO', 'VIUDO'];
const MODALIDADES = ['PRESENCIAL', 'REMOTO', 'HIBRIDO'];
const TIPOS_CUENTA = ['AHORRO', 'CORRIENTE'];

const EMPTY = {
  tipo_doc: 'DNI', num_doc: '', apellido_paterno: '', apellido_materno: '', nombres: '',
  fecha_nacimiento: '', sexo: '', estado_civil: '', direccion: '', telefono: '', email: '',
  fecha_ingreso: '', cargo_id: '', area_id: '', regimen: 'GENERAL', tipo_contrato: 'INDEFINIDO',
  jornada_horas: 8, remuneracion_basica: '', tiene_carga: false,
  sistema_pensiones: 'AFP', afp_id: '', cuspp: '',
  banco_pago: '', num_cuenta_pago: '', tipo_cuenta_pago: 'AHORRO',
  banco_cts: '', num_cuenta_cts: '', tiene_sctr: false, modalidad: 'PRESENCIAL', observaciones: ''
};

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function Trabajadores() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [trabajadores, setTrabajadores] = useState([]);
  const [afps, setAfps] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [nuevoCatOpen, setNuevoCatOpen] = useState(false);
  const [nuevoCatTipo, setNuevoCatTipo] = useState('cargo'); // 'cargo' | 'area'
  const [nuevoCatNombre, setNuevoCatNombre] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [tab, setTab] = useState(0);
  const [error, setError] = useState('');
  const [buscar, setBuscar] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('ACTIVO');
  const [cesarOpen, setCesarOpen] = useState(false);
  const [cesarId, setCesarId] = useState(null);
  const [cesarFecha, setCesarFecha] = useState('');
  const [histOpen, setHistOpen] = useState(false);
  const [histNombre, setHistNombre] = useState('');
  const [histData, setHistData] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [docMsg, setDocMsg] = useState('');

  useEffect(() => {
    axios.get('/api/rrhh/empresas').then(r => {
      setEmpresas(Array.isArray(r.data) ? r.data : []);
      if (r.data?.length === 1) setEmpresaId(r.data[0].empresa_id);
    }).catch(() => {});
    axios.get('/api/rrhh/afp').then(r => setAfps(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const [tRes, cRes, aRes] = await Promise.all([
        axios.get('/api/rrhh/trabajadores', { params: { empresa_id: empresaId, estado: filtroEstado || undefined, buscar: buscar || undefined } }),
        axios.get('/api/rrhh/cargos', { params: { empresa_id: empresaId } }),
        axios.get('/api/rrhh/areas', { params: { empresa_id: empresaId } }),
      ]);
      setTrabajadores(Array.isArray(tRes.data) ? tRes.data : []);
      setCargos(Array.isArray(cRes.data) ? cRes.data : []);
      setAreas(Array.isArray(aRes.data) ? aRes.data : []);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar'); }
    finally { setLoading(false); }
  }, [empresaId, filtroEstado, buscar]);

  useEffect(() => { cargar(); }, [cargar]);

  const empresaObj = empresas.find(e => e.empresa_id === empresaId) || null;
  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const abrirNuevoCat = (tipo) => { setNuevoCatTipo(tipo); setNuevoCatNombre(''); setNuevoCatOpen(true); };

  const handleGuardarCat = async () => {
    if (!nuevoCatNombre.trim()) return;
    try {
      const endpoint = nuevoCatTipo === 'cargo' ? 'cargos' : 'areas';
      const r = await axios.post(`/api/rrhh/${endpoint}`, { empresa_id: empresaId, nombre: nuevoCatNombre.trim() });
      if (nuevoCatTipo === 'cargo') {
        const cRes = await axios.get('/api/rrhh/cargos', { params: { empresa_id: empresaId } });
        setCargos(Array.isArray(cRes.data) ? cRes.data : []);
        sf('cargo_id', r.data.id);
      } else {
        const aRes = await axios.get('/api/rrhh/areas', { params: { empresa_id: empresaId } });
        setAreas(Array.isArray(aRes.data) ? aRes.data : []);
        sf('area_id', r.data.id);
      }
      setNuevoCatOpen(false);
    } catch (e) { setError(e.response?.data?.error || 'Error al crear'); }
  };

  const handleGuardar = async () => {
    setError('');
    try {
      const body = { ...form, empresa_id: empresaId };
      if (editId) await axios.put(`/api/rrhh/trabajadores/${editId}`, body);
      else await axios.post('/api/rrhh/trabajadores', body);
      setOpen(false);
      cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al guardar'); }
  };

  const handleEditar = (t) => {
    setEditId(t.id);
    setForm({
      tipo_doc: t.tipo_doc || 'DNI', num_doc: t.num_doc || '', apellido_paterno: t.apellido_paterno || '',
      apellido_materno: t.apellido_materno || '', nombres: t.nombres || '',
      fecha_nacimiento: t.fecha_nacimiento ? t.fecha_nacimiento.slice(0, 10) : '',
      sexo: t.sexo || '', estado_civil: t.estado_civil || '', direccion: t.direccion || '',
      telefono: t.telefono || '', email: t.email || '',
      fecha_ingreso: t.fecha_ingreso ? t.fecha_ingreso.slice(0, 10) : '',
      cargo_id: t.cargo_id || '', area_id: t.area_id || '',
      regimen: t.regimen || 'GENERAL', tipo_contrato: t.tipo_contrato || 'INDEFINIDO',
      jornada_horas: t.jornada_horas || 8,
      remuneracion_basica: t.remuneracion_basica || '', tiene_carga: !!t.tiene_carga,
      sistema_pensiones: t.sistema_pensiones || 'AFP', afp_id: t.afp_id || '', cuspp: t.cuspp || '',
      banco_pago: t.banco_pago || '', num_cuenta_pago: t.num_cuenta_pago || '', tipo_cuenta_pago: t.tipo_cuenta_pago || 'AHORRO',
      banco_cts: t.banco_cts || '', num_cuenta_cts: t.num_cuenta_cts || '',
      tiene_sctr: !!t.tiene_sctr, modalidad: t.modalidad || 'PRESENCIAL', observaciones: t.observaciones || ''
    });
    setTab(0);
    setOpen(true);
  };

  const handleCesar = async () => {
    try {
      await axios.put(`/api/rrhh/trabajadores/${cesarId}/cesar`, { fecha_cese: cesarFecha });
      setCesarOpen(false); setCesarId(null); setCesarFecha('');
      cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al cesar'); }
  };

  const handleBuscarDoc = async () => {
    const doc = form.num_doc.trim();
    setDocMsg(''); setLoadingDoc(true);
    try {
      if (form.tipo_doc === 'DNI' && doc.length === 8) {
        const d = await consultaDni(doc);
        if (!d) { setDocMsg('DNI no encontrado en RENIEC'); return; }
        sf('apellido_paterno', d.apellidoPaterno || '');
        sf('apellido_materno', d.apellidoMaterno || '');
        sf('nombres', d.nombres || '');
        setDocMsg('Datos completados desde RENIEC');
      } else if (form.tipo_doc === 'RUC' && doc.length === 11) {
        const d = await consultaRuc(doc);
        if (!d) { setDocMsg('RUC no encontrado en SUNAT'); return; }
        const nombre = d.razonSocial || d.nombre || '';
        const partes = nombre.split(' ');
        sf('apellido_paterno', partes[0] || '');
        sf('apellido_materno', partes[1] || '');
        sf('nombres', partes.slice(2).join(' ') || nombre);
        if (d.direccion) sf('direccion', d.direccion);
        setDocMsg('Datos completados desde SUNAT');
      } else {
        setDocMsg(`Se necesita ${form.tipo_doc === 'DNI' ? '8' : '11'} dígitos para consultar`);
      }
    } catch (_) {
      setDocMsg('Error al consultar');
    } finally { setLoadingDoc(false); }
  };

  const estadoColor = { ACTIVO: 'success', CESADO: 'error', SUSPENDIDO: 'warning' };

  const handleVerHistorial = async (t) => {
    setHistNombre(`${t.apellido_paterno} ${t.apellido_materno}, ${t.nombres}`);
    setHistData([]);
    setHistOpen(true);
    setHistLoading(true);
    try {
      const r = await axios.get(`/api/rrhh/trabajadores/${t.id}/historial-remuneracion`);
      setHistData(Array.isArray(r.data) ? r.data : []);
    } catch (_) {}
    setHistLoading(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1400 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 3, color: '#222' }}>Trabajadores</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Filtros */}
        <Paper sx={{ p: 2, borderRadius: 3, mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Autocomplete
              options={empresas}
              getOptionLabel={o => o?.razon_social || ''}
              value={empresaObj}
              onChange={(_, v) => setEmpresaId(v ? v.empresa_id : '')}
              isOptionEqualToValue={(o, v) => o.empresa_id === v.empresa_id}
              renderInput={p => <TextField {...p} label="Empresa" size="small" sx={{ minWidth: '16em' }} />}
              sx={{ minWidth: '16em' }}
            />
            <TextField label="Buscar" size="small" value={buscar} onChange={e => setBuscar(e.target.value)} sx={{ minWidth: '16em' }} />
            <TextField select label="Estado" size="small" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} sx={{ minWidth: '10em' }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="ACTIVO">Activo</MenuItem>
              <MenuItem value="CESADO">Cesado</MenuItem>
              <MenuItem value="SUSPENDIDO">Suspendido</MenuItem>
            </TextField>
            <Box sx={{ flexGrow: 1 }} />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditId(null); setForm(EMPTY); setTab(0); setOpen(true); }} disabled={!empresaId}>
              Nuevo Trabajador
            </Button>
          </Box>
        </Paper>

        {/* Tabla */}
        <Paper sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Documento</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Apellidos y Nombres</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Cargo / Área</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>F. Ingreso</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Remuneración</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Pensión</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trabajadores.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay trabajadores registrados</TableCell></TableRow>
                  ) : trabajadores.map(t => (
                    <TableRow key={t.id} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82em' }}>{t.tipo_doc} {t.num_doc}</TableCell>
                      <TableCell><strong>{t.apellido_paterno} {t.apellido_materno}</strong>, {t.nombres}</TableCell>
                      <TableCell sx={{ fontSize: '0.82em' }}>{t.cargo_nombre || '—'} / {t.area_nombre || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.82em' }}>{t.fecha_ingreso ? t.fecha_ingreso.slice(0, 10) : '—'}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>S/ {Number(t.remuneracion_basica || 0).toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={t.sistema_pensiones === 'AFP' ? (t.afp_nombre || 'AFP') : 'ONP'}
                          color={t.sistema_pensiones === 'AFP' ? 'primary' : 'secondary'} variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={t.estado} color={estadoColor[t.estado] || 'default'} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Historial de remuneraciones"><IconButton size="small" color="secondary" onClick={() => handleVerHistorial(t)}><HistoryIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEditar(t)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        {t.estado === 'ACTIVO' && (
                          <Tooltip title="Cese laboral">
                            <IconButton size="small" color="error" onClick={() => { setCesarId(t.id); setCesarFecha(''); setCesarOpen(true); }}>
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Dialog Trabajador */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid #eee' }}>
          {editId ? 'Editar Trabajador' : 'Nuevo Trabajador'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #eee', mb: 1 }}>
            <Tab icon={<PersonIcon />} label="Personal" iconPosition="start" />
            <Tab icon={<WorkIcon />} label="Laboral" iconPosition="start" />
            <Tab icon={<AccountBalanceIcon />} label="Previsional" iconPosition="start" />
            <Tab icon={<CreditCardIcon />} label="Bancario" iconPosition="start" />
          </Tabs>

          {/* TAB 0 — PERSONAL */}
          <TabPanel value={tab} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth label="Tipo Documento" size="small" value={form.tipo_doc} onChange={e => sf('tipo_doc', e.target.value)} sx={{ minWidth: '16em' }}>
                  {TIPOS_DOC.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="N° Documento" size="small" value={form.num_doc}
                  onChange={e => { sf('num_doc', e.target.value.replace(/\D/g, '')); setDocMsg(''); }}
                  sx={{ minWidth: '16em' }}
                  helperText={docMsg || ' '}
                  FormHelperTextProps={{ sx: { color: docMsg.startsWith('Datos') ? 'success.main' : 'text.secondary', mt: 0.2 } }}
                  InputProps={{
                    endAdornment: ['DNI', 'RUC'].includes(form.tipo_doc) ? (
                      <InputAdornment position="end">
                        <Tooltip title={form.tipo_doc === 'DNI' ? 'Consultar RENIEC' : 'Consultar SUNAT'}>
                          <span>
                            <IconButton size="small" onClick={handleBuscarDoc} disabled={loadingDoc ||
                              (form.tipo_doc === 'DNI' && form.num_doc.length !== 8) ||
                              (form.tipo_doc === 'RUC' && form.num_doc.length !== 11)}>
                              {loadingDoc ? <CircularProgress size={16} /> : <TravelExploreIcon fontSize="small" color="primary" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </InputAdornment>
                    ) : null
                  }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth label="Sexo" size="small" value={form.sexo} onChange={e => sf('sexo', e.target.value)} sx={{ minWidth: '10em' }}>
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="M">Masculino</MenuItem>
                  <MenuItem value="F">Femenino</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Apellido Paterno" size="small" value={form.apellido_paterno} onChange={e => sf('apellido_paterno', e.target.value)} sx={{ minWidth: '16em' }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Apellido Materno" size="small" value={form.apellido_materno} onChange={e => sf('apellido_materno', e.target.value)} sx={{ minWidth: '16em' }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Nombres" size="small" value={form.nombres} onChange={e => sf('nombres', e.target.value)} sx={{ minWidth: '16em' }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Fecha Nacimiento" size="small" type="date" InputLabelProps={{ shrink: true }} value={form.fecha_nacimiento} onChange={e => sf('fecha_nacimiento', e.target.value)} sx={{ minWidth: '16em' }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth label="Estado Civil" size="small" value={form.estado_civil} onChange={e => sf('estado_civil', e.target.value)} sx={{ minWidth: '16em' }}>
                  <MenuItem value="">—</MenuItem>
                  {ESTADOS_CIVIL.map(ec => <MenuItem key={ec} value={ec}>{ec}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Teléfono" size="small" value={form.telefono} onChange={e => sf('telefono', e.target.value)} sx={{ minWidth: '16em' }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email" size="small" value={form.email} onChange={e => sf('email', e.target.value)} sx={{ minWidth: '16em' }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Dirección" size="small" value={form.direccion} onChange={e => sf('direccion', e.target.value)} sx={{ minWidth: '16em' }} />
              </Grid>
            </Grid>
          </TabPanel>

          {/* TAB 1 — LABORAL */}
          <TabPanel value={tab} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Fecha Ingreso" size="small" type="date" InputLabelProps={{ shrink: true }} value={form.fecha_ingreso} onChange={e => sf('fecha_ingreso', e.target.value)} sx={{ minWidth: '16em' }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Autocomplete
                    options={cargos}
                    getOptionLabel={o => o?.nombre || ''}
                    value={cargos.find(c => c.id === form.cargo_id) || null}
                    onChange={(_, v) => sf('cargo_id', v ? v.id : '')}
                    renderInput={p => <TextField {...p} label="Cargo" size="small" sx={{ minWidth: '16em' }} />}
                    sx={{ minWidth: '16em', flexGrow: 1 }}
                  />
                  <Tooltip title="Nuevo cargo">
                    <IconButton size="small" onClick={() => abrirNuevoCat('cargo')} disabled={!empresaId}><AddIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Autocomplete
                    options={areas}
                    getOptionLabel={o => o?.nombre || ''}
                    value={areas.find(a => a.id === form.area_id) || null}
                    onChange={(_, v) => sf('area_id', v ? v.id : '')}
                    renderInput={p => <TextField {...p} label="Área" size="small" sx={{ minWidth: '16em' }} />}
                    sx={{ minWidth: '16em', flexGrow: 1 }}
                  />
                  <Tooltip title="Nueva área">
                    <IconButton size="small" onClick={() => abrirNuevoCat('area')} disabled={!empresaId}><AddIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth label="Régimen Laboral" size="small" value={form.regimen} onChange={e => sf('regimen', e.target.value)} sx={{ minWidth: '16em' }}>
                  {REGIMENES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth label="Tipo Contrato" size="small" value={form.tipo_contrato} onChange={e => sf('tipo_contrato', e.target.value)} sx={{ minWidth: '16em' }}>
                  {TIPOS_CONTRATO.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth label="Modalidad" size="small" value={form.modalidad} onChange={e => sf('modalidad', e.target.value)} sx={{ minWidth: '16em' }}>
                  {MODALIDADES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="Horas Jornada" size="small" type="number" value={form.jornada_horas} onChange={e => sf('jornada_horas', e.target.value)} sx={{ minWidth: '10em' }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Remuneración Básica (S/)" size="small" type="number" value={form.remuneracion_basica} onChange={e => sf('remuneracion_basica', e.target.value)} sx={{ minWidth: '16em' }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel control={<Checkbox checked={!!form.tiene_carga} onChange={e => sf('tiene_carga', e.target.checked)} />} label="Asignación Familiar" />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel control={<Checkbox checked={!!form.tiene_sctr} onChange={e => sf('tiene_sctr', e.target.checked)} />} label="Tiene SCTR" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Observaciones" size="small" multiline rows={2} value={form.observaciones} onChange={e => sf('observaciones', e.target.value)} />
              </Grid>
            </Grid>
          </TabPanel>

          {/* TAB 2 — PREVISIONAL */}
          <TabPanel value={tab} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth label="Sistema de Pensiones" size="small" value={form.sistema_pensiones} onChange={e => sf('sistema_pensiones', e.target.value)} sx={{ minWidth: '16em' }}>
                  <MenuItem value="AFP">AFP</MenuItem>
                  <MenuItem value="ONP">ONP</MenuItem>
                </TextField>
              </Grid>
              {form.sistema_pensiones === 'AFP' && (
                <>
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={afps}
                      getOptionLabel={o => o?.nombre || ''}
                      value={afps.find(a => a.id === form.afp_id) || null}
                      onChange={(_, v) => sf('afp_id', v ? v.id : '')}
                      renderInput={p => <TextField {...p} label="AFP" size="small" sx={{ minWidth: '16em' }} />}
                      sx={{ minWidth: '16em' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="CUSPP" size="small" value={form.cuspp} onChange={e => sf('cuspp', e.target.value)} sx={{ minWidth: '16em' }} />
                  </Grid>
                </>
              )}
              {form.afp_id && (() => {
                const afp = afps.find(a => a.id === form.afp_id);
                if (!afp) return null;
                return (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <strong>{afp.nombre}</strong> — Aporte: {(afp.aporte_obligatorio * 100).toFixed(2)}% | Comisión flujo: {(afp.comision_flujo * 100).toFixed(2)}% | Prima seguro: {(afp.prima_seguro * 100).toFixed(2)}%<br />
                      <em>Total descuento trabajador ≈ {((afp.aporte_obligatorio + afp.comision_flujo + afp.prima_seguro) * 100).toFixed(2)}% de la remuneración bruta</em>
                    </Alert>
                  </Grid>
                );
              })()}
              {form.sistema_pensiones === 'ONP' && (
                <Grid item xs={12}>
                  <Alert severity="info">ONP: descuento del 13% de la remuneración bruta. Sin cobertura AFP.</Alert>
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* TAB 3 — BANCARIO */}
          <TabPanel value={tab} index={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">CUENTA DE PAGO DE REMUNERACIONES</Typography></Divider></Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Banco (Pago)" size="small" value={form.banco_pago} onChange={e => sf('banco_pago', e.target.value)} sx={{ minWidth: '16em' }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="N° Cuenta (Pago)" size="small" value={form.num_cuenta_pago} onChange={e => sf('num_cuenta_pago', e.target.value)} sx={{ minWidth: '16em' }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth label="Tipo Cuenta (Pago)" size="small" value={form.tipo_cuenta_pago} onChange={e => sf('tipo_cuenta_pago', e.target.value)} sx={{ minWidth: '16em' }}>
                  {TIPOS_CUENTA.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12}><Divider sx={{ mt: 1 }}><Typography variant="caption" color="text.secondary">CUENTA CTS</Typography></Divider></Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Banco CTS" size="small" value={form.banco_cts} onChange={e => sf('banco_cts', e.target.value)} sx={{ minWidth: '16em' }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="N° Cuenta CTS" size="small" value={form.num_cuenta_cts} onChange={e => sf('num_cuenta_cts', e.target.value)} sx={{ minWidth: '16em' }} />
              </Grid>
            </Grid>
          </TabPanel>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardar}>
            {editId ? 'Actualizar' : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Cese */}
      <Dialog open={cesarOpen} onClose={() => setCesarOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Cese Laboral</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Fecha de Cese" type="date" size="small" InputLabelProps={{ shrink: true }}
            value={cesarFecha} onChange={e => setCesarFecha(e.target.value)} sx={{ mt: 1, minWidth: '16em' }} />
          <Alert severity="warning" sx={{ mt: 2 }}>El trabajador quedará en estado CESADO. Esta acción se puede revertir editando su ficha.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCesarOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleCesar} disabled={!cesarFecha}>Confirmar Cese</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Historial de Remuneraciones */}
      <Dialog open={histOpen} onClose={() => setHistOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'purple', color: 'white' }}>
          Historial de Remuneraciones — {histNombre}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {histLoading ? (
            <Box p={3} textAlign="center"><CircularProgress color="secondary" /></Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f3e5f5' }}>
                    {['Fecha vigencia', 'Remuneración anterior', 'Remuneración nueva', 'Variación', 'Motivo', 'Usuario'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: 12 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {histData.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: '#888' }}>
                      No hay cambios de remuneración registrados
                    </TableCell></TableRow>
                  ) : histData.map((h, i) => {
                    const diff = Number(h.remuneracion_nueva) - Number(h.remuneracion_anterior);
                    return (
                      <TableRow key={i} hover>
                        <TableCell sx={{ fontSize: 12 }}>{h.fecha_vigencia?.substring(0, 10)}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>
                          S/ {Number(h.remuneracion_anterior).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 'bold' }}>
                          S/ {Number(h.remuneracion_nueva).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', color: diff >= 0 ? '#2e7d32' : '#c62828', fontWeight: 'bold' }}>
                          {diff >= 0 ? '+' : ''}{diff.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{h.motivo || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 11, color: '#666' }}>{h.usuario_reg}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={nuevoCatOpen} onClose={() => setNuevoCatOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{nuevoCatTipo === 'cargo' ? 'Nuevo Cargo' : 'Nueva Área'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth size="small" sx={{ mt: 1 }}
            label={nuevoCatTipo === 'cargo' ? 'Nombre del cargo' : 'Nombre del área'}
            value={nuevoCatNombre}
            onChange={e => setNuevoCatNombre(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleGuardarCat(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNuevoCatOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardarCat} disabled={!nuevoCatNombre.trim()}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
